import type { ReactNode } from 'react'
import { DashButton } from './DashPrimitives'

/**
 * The four data states, as ONE wrapper every data surface uses.
 *
 * design-system.md §8: "Every data surface ships four states."
 *
 *   default   the data
 *   loading   🔴 SKELETAL, NOT SPINNING — a skeleton says what is coming;
 *             a spinner says only "wait", and on a slow school laptop it says
 *             it for a long time
 *   empty     🔴 a sentence plus ONE action — never blank white space.
 *             "Blank space where an empty state belongs" is on the Never list
 *   error     🔴 what failed, WHEN IT LAST WORKED, and a retry. "Couldn't load
 *             this week / Last synced yesterday at 6:40 pm / Try again".
 *             The middle clause is the one that gets dropped, and it is the one
 *             that tells a teacher whether to panic
 *
 * 🔴 A ZERO NEVER CONTRADICTS THE PANEL BESIDE IT. If there is no data, both
 * say so. The predecessor shipped "0 games played" beside "Animal Walk 6×".
 * That is why this is one wrapper rather than four ad-hoc branches per screen:
 * a surface either has data or it does not, and every panel on it agrees.
 */
export type SurfaceState = 'ready' | 'loading' | 'empty' | 'error'

export interface DataSurfaceProps {
  state: SurfaceState
  children: ReactNode

  /** Rows of skeleton to draw while loading. */
  skeletonRows?: number

  /** One sentence. Not a paragraph, not an apology. */
  emptyMessage?: ReactNode
  emptyActionLabel?: ReactNode
  onEmptyAction?: () => void

  /** What failed. */
  errorMessage?: ReactNode
  /** 🔴 When it last worked. Do not omit this. */
  errorLastWorked?: ReactNode
  errorRetryLabel?: ReactNode
  onRetry?: () => void
}

function Skeleton({ rows }: { rows: number }) {
  return (
    <div className="flex flex-col gap-3" aria-busy="true" aria-live="polite">
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          className="h-4 animate-pulse rounded-pill"
          style={{
            background: 'var(--color-pill-neutral-bg)',
            // Uneven widths read as content arriving; equal bars read as a
            // progress bar that never moves.
            width: `${[92, 78, 85, 64][i % 4]}%`,
          }}
        />
      ))}
    </div>
  )
}

export function DataSurface({
  state,
  children,
  skeletonRows = 4,
  emptyMessage,
  emptyActionLabel,
  onEmptyAction,
  errorMessage,
  errorLastWorked,
  errorRetryLabel,
  onRetry,
}: DataSurfaceProps) {
  if (state === 'loading') return <Skeleton rows={skeletonRows} />

  if (state === 'empty') {
    return (
      <div className="flex flex-col items-start gap-3 font-dash">
        <p className="text-dash-md text-ink-soft">{emptyMessage}</p>
        {emptyActionLabel ? (
          <DashButton variant="secondary" onClick={onEmptyAction}>
            {emptyActionLabel}
          </DashButton>
        ) : null}
      </div>
    )
  }

  if (state === 'error') {
    return (
      <div className="flex flex-col items-start gap-3 font-dash" role="alert">
        <p className="text-dash-md font-bold text-ink">{errorMessage}</p>
        {/* 🔴 When it last worked. This is the clause that gets dropped. */}
        {errorLastWorked ? <p className="text-dash-sm text-muted">{errorLastWorked}</p> : null}
        {errorRetryLabel ? (
          <DashButton variant="secondary" onClick={onRetry}>
            {errorRetryLabel}
          </DashButton>
        ) : null}
      </div>
    )
  }

  return <>{children}</>
}
